import { type FC, useEffect, useId, useRef } from 'react';
import mermaid from 'mermaid';

import { Box } from '@thirdspacelearning/library';

type ProgrammeConnectionsDiagramProps = {
    diagram: string;
    onProgrammeClick: (programmeId: number) => void;
};

let isMermaidInitialized = false;

const initialiseMermaid = () => {
    if (isMermaidInitialized) {
        return;
    }

    mermaid.initialize({
        startOnLoad: false,
        securityLevel: 'loose',
        flowchart: {
            htmlLabels: true,
            useMaxWidth: false,
        },
    });

    isMermaidInitialized = true;
};

const ProgrammeConnectionsDiagram: FC<ProgrammeConnectionsDiagramProps> = ({
    diagram,
    onProgrammeClick,
}) => {
    const diagramId = useId().replace(/:/g, '-');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        initialiseMermaid();

        let isCancelled = false;

        const renderDiagram = async () => {
            if (!containerRef.current) {
                return;
            }

            const { svg } = await mermaid.render(
                `programme-connections-${diagramId}`,
                diagram,
            );

            if (!containerRef.current || isCancelled) {
                return;
            }

            containerRef.current.innerHTML = svg;

            containerRef.current
                .querySelectorAll<SVGGElement>('g.node')
                .forEach((node) => {
                    const match = node.id.match(/programme_(\d+)/);

                    if (!match) {
                        return;
                    }

                    node.style.cursor = 'pointer';
                    node.addEventListener('click', () =>
                        onProgrammeClick(Number(match[1])),
                    );
                });
        };

        void renderDiagram();

        return () => {
            isCancelled = true;
        };
    }, [diagram, diagramId, onProgrammeClick]);

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                '& svg': {
                    display: 'block',
                    height: 'auto',
                },
            }}
        />
    );
};

export default ProgrammeConnectionsDiagram;
